import { useEffect, useRef, useState } from 'react';
import api from '../../../api/axiosInstance';
import axios from 'axios';

export const useFetchLogs = (logsUrl: string, params?: any) => {
  const [logs, setLogs] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fetchIdRef = useRef(0);

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();
    const currentFetchId = ++fetchIdRef.current;

    const fetchLogs = async () => {
      try {
        if (!logsUrl) {
          if (isMounted && fetchIdRef.current === currentFetchId) {
            setLogs('');
            setError(null);
            setIsLoading(false);
          }
          return;
        }

        if (logsUrl.includes('eric-adp-da.com')) {
          if (isMounted && fetchIdRef.current === currentFetchId) {
            setLogs('Log is not available for this step.');
            setError(null);
            setIsLoading(false);
          }
          return;
        }
        setIsLoading(true);

        if (isMounted && fetchIdRef.current === currentFetchId) setError(null);

        const response = await api.post('/api/workflows/logs', { logUrl: logsUrl }, { signal: controller.signal });
        const parsedLogs = response.data.parsedLogs || 'Log is not available for this step at the moment.';

        if (isMounted && fetchIdRef.current === currentFetchId) {
          setLogs(parsedLogs);
          setError(null);
        }
      } catch (err: unknown) {
        if (axios.isCancel(err) || (err && typeof err === 'object' && 'name' in err && err.name === 'AbortError')) return;
        console.error('[fetchLogs] Error occurred:', err);
        if (isMounted && fetchIdRef.current === currentFetchId) {
          const msg = axios.isAxiosError(err)
            ? err.response?.data?.message || err.message || 'Failed to fetch logs'
            : err instanceof Error ? err.message : 'Failed to fetch logs';
          setError(msg);
          setLogs('');
        }
      } finally {
        if (isMounted && fetchIdRef.current === currentFetchId) setIsLoading(false);
      }
    };

    const fetchNodeLogs = async () => {
      try {
        if (!logsUrl) {
          if (isMounted && fetchIdRef.current === currentFetchId) {
            setLogs('');
            setError(null);
            setIsLoading(false);
          }
          return;
        }
        setIsLoading(true);
        if (isMounted && fetchIdRef.current === currentFetchId) setError(null);

        const response = await api.post('/api/workflows/logs', { logUrl: logsUrl, isNodeLogs: true }, { signal: controller.signal });
        const parsedLogs = response.data.parsedLogs || 'Log is not available at this moment.';

        if (isMounted && fetchIdRef.current === currentFetchId) {
          setLogs(parsedLogs);
          setError(null);
        }
      } catch (err: unknown) {
        if (axios.isCancel(err) || (err && typeof err === 'object' && 'name' in err && err.name === 'AbortError')) return;
        console.error('[fetchNodeLogs] Error occurred:', err);
        if (isMounted && fetchIdRef.current === currentFetchId) {
          const msg = axios.isAxiosError(err)
            ? err.response?.data?.message || err.message || 'Failed to fetch node logs'
            : err instanceof Error ? err.message : 'Failed to fetch node logs';
          setError(msg);
          setLogs('');
        }
      } finally {
        if (isMounted && fetchIdRef.current === currentFetchId) setIsLoading(false);
      }
    };

    if (params?.isNodeLogs) {
      fetchNodeLogs();
    } else {
      fetchLogs();
    }
    return () => { isMounted = false; controller.abort(); };
  }, [logsUrl]);

  return { logs, isLoading, error };
};
